"use client";

import Image from "next/image";
import {
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { DOOR_LABEL, SHIPPED_DOORS, type Door } from "../_content/card";
import { Icon } from "../_family/Icon";
import type { Token } from "./colour";
import { CopyLine } from "./CopyLine";
import { Tokens } from "./Tokens";
import { HERO_CODE } from "./words";

/**
 * The hero's doors: pick yours, and its first line (its install, or for HTTP and MCP the line you start with)
 * is there to copy, and the code window shows the same job through it, with Bash sitting on the window's bar.
 * Beside the window, what traveller-42 sees (children). The arrows move between doors, as a radio group's do.
 */
export function HeroDoors({
  installs,
  code,
  children,
}: {
  installs: Record<Door, string>;
  code: Record<Door, Token[][][]>;
  children: ReactNode;
}) {
  const [door, setDoor] = useState<Door>("ts");
  const radios = useRef<Array<HTMLButtonElement | null>>([]);

  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const step =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? SHIPPED_DOORS.length - 1
          : 0;
    if (!step) return;
    e.preventDefault();
    const next = (i + step) % SHIPPED_DOORS.length;
    setDoor(SHIPPED_DOORS[next]);
    radios.current[next]?.focus();
  };

  return (
    <>
      <div className="hx-tabsrow">
        <div
          className="hx-doors"
          role="radiogroup"
          aria-label="Your door"
          aria-controls="vf-hero-code"
        >
          {SHIPPED_DOORS.map((d, i) => (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={d === door}
              tabIndex={d === door ? 0 : -1}
              ref={(el) => {
                radios.current[i] = el;
              }}
              onClick={() => setDoor(d)}
              onKeyDown={(e) => onKey(e, i)}
            >
              {DOOR_LABEL[d]}
            </button>
          ))}
        </div>
        <div className="hx-install">
          <code title={installs[door]}>{installs[door]}</code>
          <CopyLine
            text={installs[door]}
            label={`Copy the ${DOOR_LABEL[door]} line`}
          />
        </div>
      </div>
      <div className="hx-panel">
        <div className="hx-ed">
          <div className="hx-sit" aria-hidden="true">
            <Image
              src="/developers/family/sit-bash.webp"
              width={238}
              height={560}
              alt=""
              loading="eager"
              unoptimized
            />
            <span className="hx-steam">
              <i />
              <i />
              <i />
            </span>
          </div>
          <div className="edwin" id="vf-hero-code">
            <div className="edwin-bar">
              <span className="lights">
                <i />
                <i />
                <i />
              </span>
              <span className="hx-tab">{HERO_CODE.file[door]}</span>
            </div>
            {/* Every door's code is on the page; the one you picked shows, typing itself in. */}
            {SHIPPED_DOORS.map((d) => (
              <div
                key={d}
                className="hx-pane"
                data-door={d}
                hidden={d !== door}
              >
                {code[d].map((sample, s) => (
                  <div key={s} className="hx-sample">
                    {d === "mcp" && (
                      <p className="hx-cap">
                        <Icon name="mcp" />
                        {HERO_CODE.mcp[s]}
                      </p>
                    )}
                    <pre className="hx-code">
                      <code>
                        {sample.map((line, i) => (
                          <span
                            key={i}
                            className="hx-ln"
                            style={{ "--i": i } as CSSProperties}
                          >
                            {line.length > 0 && <Tokens tokens={line} />}
                            {"\n"}
                          </span>
                        ))}
                      </code>
                    </pre>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        {children}
      </div>
    </>
  );
}
